import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/notificationsSlice';
import { addMessage, updateMessageStatus, markAsRead } from '../store/slices/messagesSlice';
import { CollaborationMessage, Notification } from '../types';
import { notificationSound } from '../utils/notificationSound';
import useIdle from '../hooks/useIdle';

interface WebSocketContextType {
  socket: WebSocket | null;
  isConnected: boolean;
  sendMessage: (message: any) => void;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'reconnecting';
  lastMessage: any;
}

const WebSocketContext = createContext<WebSocketContextType>({
  socket: null,
  isConnected: false,
  sendMessage: () => {},
  connectionStatus: 'disconnected',
  lastMessage: null,
});

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const socketRef = useRef<WebSocket | null>(null);
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isIdle = useIdle(30000); // 30 seconds idle timeout
  
  // Use refs to access latest state in WebSocket callbacks without triggering reconnection
  const locationRef = useRef(location);
  const isIdleRef = useRef(isIdle);

  useEffect(() => {
    locationRef.current = location;
    isIdleRef.current = isIdle;
  }, [location, isIdle]);
  
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const [lastMessage, setLastMessage] = useState<any>(null);
  
  // Reconnection logic
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const baseReconnectDelay = 1000; // Start with 1 second

  // Calculate exponential backoff delay
  const getReconnectDelay = () => {
    return Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts.current), 30000); // Cap at 30 seconds
  };

  const connect = React.useCallback(() => {
    if (!user?.id) {
      // console.log('No user ID available for WebSocket connection');
      return;
    }

    // Don't create a new connection if one already exists and is open
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      // console.log('WebSocket already connected');
      return;
    }

    if (socketRef.current && (socketRef.current.readyState === WebSocket.CONNECTING || socketRef.current.readyState === WebSocket.OPEN)) {
      return; // Already connecting or connected
    }

    // Clean up existing socket if it's closing or closed
    if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
    }

    const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'wss://staging.13.204.65.150.nip.io/ws';
    const WS_URL = `${WS_BASE_URL.includes('/ws') ? WS_BASE_URL : `${WS_BASE_URL}/ws`}?user_id=${user.id}`;
    // console.log('🔄 Attempting WebSocket connection...');
    setConnectionStatus('connecting');

    try {
      socketRef.current = new WebSocket(WS_URL);

      socketRef.current.onopen = () => {
        // console.log('✅ WebSocket connected successfully');
        setIsConnected(true);
        setConnectionStatus('connected');
        reconnectAttempts.current = 0; // Reset reconnect attempts on successful connection
        
        // Clear any existing reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      socketRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          // console.log('📨 WebSocket message received:', message);
          setLastMessage(message);

          switch (message.type) {
            case 'new_message':
                // Handle new message - add to conversation messages
                // console.log('💬 New message:', message.data);
                
                // Add message to messages state for real-time updates
                if (message.data.message) {
                  dispatch(addMessage(message.data.message));
                }

                // Play sound logic
                // 1. If tab is hidden (background) -> Play
                // 2. If NOT on messages page -> Play
                // 3. If on messages page BUT idle -> Play
                const currentPath = locationRef.current.pathname;
                const isMessagesPage = currentPath.startsWith('/messages') || currentPath.startsWith('/chat');
                const isTabHidden = document.hidden;
                const isUserIdle = isIdleRef.current;

                if (isTabHidden || !isMessagesPage || isUserIdle) {
                    notificationSound.play();
                } else {
                    // If we are active, on the messages page, and NOT idle, mark as read immediately
                    // Check if we are viewing the specific conversation
                    const msgConvId = message.data.message.conversation_id;
                    // Assuming URL is /chat/:id or /messages (if /messages, we might be viewing the list, not the chat?)
                    // If /messages, we might be on the list. If /chat/:id, we are on the chat.
                    // But wait, the user might be on /messages but with a selected conversation?
                    // The URL structure seems to be /chat/:conversationId for the chat view.
                    
                    if (currentPath.includes(msgConvId)) {
                        dispatch(markAsRead(msgConvId));
                    }
                }
                break;

            case 'notification_update':
               // console.log('🔔 Notification update:', message.data);
               dispatch(addNotification(message.data));
               notificationSound.play();
               break;

            case 'notification':
              // Handle new notification
              // console.log('🔔 New notification:', message.data);
              dispatch(addNotification(message.data));
              notificationSound.play();
              break;
              
            case 'collaboration_invite':
              // Handle collaboration invite
              // console.log('🤝 Collaboration invite:', message.data);
              dispatch(addNotification(message.data));
              notificationSound.play();
              break;

            case 'collaboration_request':
                // console.log('🤝 New collaboration request:', message.data);
                handleCollaborationRequest(message.data as CollaborationMessage);
                break;
                
            case 'collaboration_response':
                // console.log('✅ Collaboration response:', message.data);
                handleCollaborationResponse(message.data as CollaborationMessage);
                break;
                
            case 'collaboration_reminder':
                // console.log('⏰ Collaboration reminder:', message.data);
                handleCollaborationReminder(message.data as CollaborationMessage);
            break;
              
            default:
              // console.log('❓ Unknown message type:', message.type);
          }
        } catch (error) {
          console.error('❌ Error parsing WebSocket message:', error);
        }
      };

      socketRef.current.onclose = (event) => {
        // console.log('🔌 WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        setConnectionStatus('disconnected');

        // Only attempt to reconnect if not intentionally closed
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts && user?.id) {
          reconnectAttempts.current++;
          const delay = getReconnectDelay();
          
          // console.log(`🔄 Scheduling reconnection attempt ${reconnectAttempts.current}/${maxReconnectAttempts} in ${delay}ms`);
          setConnectionStatus('reconnecting');
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          console.error('❌ Max reconnection attempts reached');
          setConnectionStatus('disconnected');
        }
      };

      socketRef.current.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setConnectionStatus('disconnected');
      };

    } catch (error) {
      console.error('❌ WebSocket connection failed:', error);
      setConnectionStatus('disconnected');
    }
  }, [user?.id, dispatch]); // Removed location and isIdle dependencies

  const sendMessage = React.useCallback((message: any) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
      // console.log('📤 Message sent:', message);
    } else {
      console.warn('⚠️ WebSocket not connected, cannot send message:', message);
      
      // Optionally queue messages when disconnected
      // You could implement a message queue here
    }
  }, []);

  // Connect when user is available and clean up on unmount
  useEffect(() => {
    if (user?.id) {
      connect();
    }

    return () => {
      // console.log('🧹 Cleaning up WebSocket connection');
      
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Close socket connection
      if (socketRef.current) {
        socketRef.current.close(1000, 'Component unmounting');
        socketRef.current = null;
      }
      
      setIsConnected(false);
      setConnectionStatus('disconnected');
    };
  }, [connect]);

  // Handle user changes (login/logout)
  useEffect(() => {
    if (!user?.id && socketRef.current) {
      // User logged out, close connection
      // console.log('🔐 User logged out, closing WebSocket connection');
      socketRef.current.close(1000, 'User logged out');
      setIsConnected(false);
      setConnectionStatus('disconnected');
    } else if (user?.id && !isConnected) {
      // User logged in but not connected, connect
      // console.log('🔐 User logged in, establishing WebSocket connection');
      connect();
    }
  }, [user?.id, isConnected, connect]);

  // Add connection status logging
  useEffect(() => {
    // console.log(`🔗 WebSocket connection status: ${connectionStatus}`);
  }, [connectionStatus]);

  const contextValue = React.useMemo(() => ({
    socket: socketRef.current,
    isConnected,
    sendMessage,
    connectionStatus,
    lastMessage,
  }), [isConnected, sendMessage, connectionStatus, lastMessage]);

  // Add these handler functions to your context
const handleCollaborationRequest = (data: CollaborationMessage) => {
    // Create notification for new collaboration request
    const collaborationNotification: Notification = {
      id: `${data.request_id}`,
      user_id: user?.id,
      type: 'collaboration_invite',
      title: `New collaboration request from ${data.sender_info.name}`,
      message: data.message,
      action_type: data.action_type || 'view_request',
      reference_id: data.request_id,
      reference_type: 'collaboration_request',
      is_read: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  
    dispatch(addNotification(collaborationNotification));
    notificationSound.play();
  
    // Show toast notification (optional - you can remove if you don't want it)
    // toast.success(`New collaboration request from ${data.sender_info.name}`, {
    //   duration: 6000,
    //   icon: '🤝',
    // });
  };
  
  const handleCollaborationResponse = (data: CollaborationMessage) => {
    const isAccepted = data.type === 'request_accepted';
    
    const responseNotification: Notification = {
      id: data.notification_id || `collab-response-${data.request_id}`, // Fallback for backward compatibility
      user_id: user?.id,
      type: isAccepted ? 'collaboration_accept' : 'collaboration_reject',
      title: isAccepted ? 'Collaboration Request Accepted!' : 'Collaboration Request Declined',
      message: data.message,
      action_type: data.action_type || (isAccepted ? 'open_chat' : 'view_request'),
      reference_id: isAccepted ? data.sender_info.id : data.request_id,
      reference_type: isAccepted ? 'conversation' : 'collaboration_request',
      is_read: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  
    dispatch(addNotification(responseNotification));
    notificationSound.play();
  };
  
  const handleCollaborationReminder = (data: CollaborationMessage) => {
    const reminderNotification: Notification = {
      id: `collab-reminder-${data.request_id}`,
      user_id: user?.id,
      type: 'collaboration_update',
      title: 'Collaboration Request Reminder',
      message: data.message,
      action_type: 'view_request',
      reference_id: data.request_id,
      reference_type: 'collaboration_request',
      is_read: false,
      expires_at: data.expires_at ? new Date(data.expires_at * 1000).toISOString() : undefined,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  
    dispatch(addNotification(reminderNotification));
    notificationSound.play();
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};
